import { LocalStorageService } from 'angular-2-local-storage';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { BbTableService, BbTableSelectOption, TableRow } from '../../../services/bb-table.service';
import { OverlayService, DialogDefinition } from '../../../services/overlay.service';
import { LocationService } from '../../../services/location.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { AbstractListComponent } from '../../shared/abstract-list.component';
import { share } from 'rxjs/operators';

@Component({
	selector: 'trash-list',
	templateUrl: './trash-list.component.html',
	styleUrls: ['./trash-list.component.scss'],
	providers: [BbTableService, LocationService]
})

export class TrashListComponent extends AbstractListComponent implements OnInit {

	putRight = false;
	deleteRight = false;
	totalAmount = 0;
	currentFilter = 'all';

	constructor(protected bbTableService: BbTableService,
		protected http: HttpClient,
		protected localStorageService: LocalStorageService,
		protected overlayService: OverlayService,
		protected translate: TranslateService,
		protected locationService: LocationService,
		protected authenticationService: AuthenticationService) {

		super(bbTableService);

		this.listConfiguration = {
			name: 'mediaclip',
			basePath: '/library/trash'
		};

		this.filterConfig = {
			search: true,
			filters: [],
			presets: [{
				label: this.translate.instant('ALL'),
				value: 'all',
				filterSet: [],
			},
			{
				label: this.translate.instant('VIDEO'),
				value: 'video',
				filterSet: [{
					filters: [{
						type: 'mediaclip',
						field: 'mediatype',
						operator: 'is',
						value: 'video'
					}]
				}],
			},
			{
				label: this.translate.instant('AUDIO'),
				value: 'audio',
				filterSet: [{
					filters: [{
						type: 'mediaclip',
						field: 'mediatype',
						operator: 'is',
						value: 'audio'
					}]
				}],
			},
			{
				label: this.translate.instant('IMAGE'),
				value: 'image',
				filterSet: [{
					filters: [{
						type: 'mediaclip',
						field: 'mediatype',
						operator: 'is',
						value: 'image'
					}]
				}],
			},
			{
				label: this.translate.instant('DOCUMENT'),
				value: 'document',
				filterSet: [{
					filters: [{
						type: 'mediaclip',
						field: 'mediatype',
						operator: 'is',
						value: 'document'
					}]
				}],
			}]

		};

		this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight);
		this.authenticationService.hasPermission('delete').subscribe(hasRight => this.deleteRight = hasRight);
		this.bbTableService.totalChangedObservable.subscribe(amount => this.totalAmount = amount);
	}

	ngOnInit() {

		this.bbTableService
			.setSelect(BbTableSelectOption.multi)
			.setLimit(15)
			.sort('updateddate', 'desc')
			.setFilterParam('showDeleted', 'true')
			.setFilterQuery('status:deleted')
			.linkToUrl();

		super.ngOnInit();
	}

	redirectToDetails(row: TableRow) {
		return false;
	}

	restoreMediaclip(row: TableRow) {
		const mediaclipRow = row;

		const sessToken = this.localStorageService.get('ovp-sessionToken');
		const headers = new HttpHeaders()
			.append('sesstoken', (sessToken ? sessToken.toString() : ''));

		const requestOptions = { headers, withCredentials: true };

		if (mediaclipRow.data) {
			const mediaclips = this.bbTableService.getSelectedRows();

			const items: string[] = [];

			for (let i = 0; i < mediaclips.length; i++) {
				const mediaclip = mediaclips[i];
				items.push(mediaclip.id);
			}

			const observable = this.http.put(this.locationService.server
				+ '/sapi/mediaclip/batch', [{ property: 'status', action: 'set', value: 'draft', items }], requestOptions).pipe(share());
			observable.subscribe(() => {
				this.bbTableService.removeRows(items);
				this.overlayService.createToast(this.translate.instant('MEDIACLIP_PUT_BACK_BATCH', { items }), 'check');
			});

		} else {
			if (mediaclipRow && mediaclipRow.id) {
				const observable = this.http.put(this.locationService.server + '/sapi/mediaclip/' + mediaclipRow.id,
					{ id: mediaclipRow.id, status: 'draft' }, requestOptions).pipe(share());
				observable.subscribe(() => {
					this.bbTableService.removeRows([mediaclipRow.id]);
					this.overlayService.createToast(this.translate.instant('ENTITY_RESTORED_TO_DRAFT',
						{ entity: 'mediaclip', id: mediaclipRow.id }), 'check');
				});
			}
		}
	}

	purgeMediaclip(row: TableRow) {
		const mediaclip = row;

		const sessToken = this.localStorageService.get('ovp-sessionToken');
		const headers = new HttpHeaders()
			.append('sesstoken', (sessToken ? sessToken.toString() : ''));

		const requestOptions = { headers, withCredentials: true, params: new HttpParams() };

		let title: string;
		let mediaclips: TableRow[];
		let contents: string;

		if (mediaclip.data) {
			mediaclips = this.bbTableService.getSelectedRows();
			title = this.translate.instant('PURGE_CLIPS', { count: mediaclips.length });
			contents = this.translate.instant('PURGE_CLIPS_SURE', { count: mediaclips.length });
			requestOptions.params = requestOptions.params.append('absolutelySureWhatIAmDoing', 'true');
		} else {
			title = this.translate.instant('PURGE_CLIP', { mediaclip_id: mediaclip.id });
			contents = this.translate.instant('PURGE_CLIP_SURE', { mediaclip_id: mediaclip.id });
			requestOptions.params = requestOptions.params.append('purge', 'true');
		}

		const dialog: DialogDefinition = {
			icon: 'trash-o',
			title,
			contents,
			onConfirm: () => {
				if (mediaclips && mediaclips.length > 0) {
					const items: string[] = [];

					for (let i = 0; i < mediaclips.length; i++) {
						items.push(mediaclips[i].id);
					}

					const observable = this.http.put(this.locationService.server + '/sapi/mediaclip/batch',
						[{ action: 'purge', items, absolutelySureWhatIAmDoing: 'true' }], requestOptions).pipe(share());
					observable.subscribe(() => {
						this.bbTableService.removeRows(items);
						this.overlayService.createToast(this.translate.instant('PURGE_CLIPS_SUCCESS', { count: items.length }), 'check');
					});

				} else {
					if (mediaclip && mediaclip.id) {
						const observable = this.http.delete(this.locationService.server + '/sapi/mediaclip/' + mediaclip.id, requestOptions).pipe(share());
						observable.subscribe(() => {
							this.bbTableService.removeRows([mediaclip.id]);
							this.overlayService.createToast(this.translate.instant('PURGE_CLIP_SUCCESS',
								{ entity: 'mediaclip', mediaclip_id: mediaclip.id }), 'check');
						});
					}
				}
			}
		};

		this.overlayService.createDialog(dialog);
	}

	purgeTrashItems() {
		const dialog: DialogDefinition = {
			icon: 'trash-o',
			title: this.translate.instant('PURGE_ALL_CLIPS'),
			contents: this.translate.instant('PURGE_CLIPS_SURE', { count: this.bbTableService.totalAmount }),
			onConfirm: () => {
				let headers = new HttpHeaders();

				const sessToken = this.localStorageService.get('ovp-sessionToken');
				headers = headers.append('sesstoken', (sessToken ? sessToken.toString() : ''));

				const requestOptions = { headers, withCredentials: true, params: new HttpParams() };
				requestOptions.params = requestOptions.params.set('absolutelySureWhatIAmDoing', 'true');

				const observable = this.http.get(this.locationService.server + '/sapi/mediaclip/purgeAll', requestOptions)
					.pipe(
						share()
					);
				observable.subscribe(() => {
					this.overlayService.createToast(this.translate.instant('SUCCESS_PURGING_TRASH'), 'check');
				});

				return observable;
			}
		};

		this.overlayService.createDialog(dialog);
	}
}
